class GiftEngine {
  constructor() {
    this.EVENT_NAME = "eurus:cart:items-changed";
    this.INTERNAL_EVENT = "gift:cart:changed";
    this.ACTION_COOLDOWN_MS = 400;

    this.isUpdating = false;
    this.lastActionAt = 0;
  }

  now() {
    return Date.now();
  }

  normalizeGidToNumeric(id) {
    if (!id) return "";
    const match = String(id).match(/\/(\d+)$/);
    return match ? match[1] : String(id);
  }

  safeArray(value) {
    return Array.isArray(value) ? value : [];
  }

  async getCart() {
    const response = await fetch("/cart.js", { credentials: "same-origin" });
    return response.json();
  }

  isGiftItem(item) {
    return item?.properties?._gift == "true";
  }

  findGiftItem(cart, variantId) {
    return (
      this.safeArray(cart.items).find(
        item => this.isGiftItem(item) && String(item.variant_id) === String(variantId)
      ) || null
    );
  }

  getCampaign() {
    return window.GIFT_CAMPAIGN || null;
  }

  getGiftVariantId(campaign) {
    return this.normalizeGidToNumeric(
      campaign?.hediye_urun ||
      campaign?.gift_variant_id ||
      campaign?.gift_variant
    );
  }

  getTiers() {
    const tiers = window.GIFT_CAMPAIGN_TIERS;
    if (!Array.isArray(tiers) || tiers.length === 0) return null;

    return tiers
      .map(t => ({
        min_tutar: Number(t.min_tutar) || 0,
        indirim_yuzdesi: Number(t.indirim_yuzdesi) || 0
      }))
      .sort((a, b) => b.min_tutar - a.min_tutar);
  }

  findActiveTier(tiers, total) {
    return tiers.find(t => total >= t.min_tutar * 100) || null;
  }

  getCartTotal(cart) {
    return this.safeArray(cart.items)
      .filter(item => !this.isGiftItem(item))
      .reduce((sum, item) => sum + item.original_line_price, 0);
  }

  getTargetTotal(cart, campaign, collectionData) {
    const items = this.filterTargetItems(cart, campaign, collectionData);
    return items.reduce((sum, item) => sum + item.original_line_price, 0);
  }

  normalizeMap(map) {
    const output = {};

    Object.keys(map || {}).forEach(productId => {
      output[String(productId)] = this.safeArray(map[productId]).map(id =>
        this.normalizeGidToNumeric(id)
      );
    });

    return output;
  }

  async fetchCollectionData() {
    try {
      const response = await fetch(
        "/?sections=gift-collection-data",
        { credentials: "same-origin" }
      );
      const data = await response.json();
      const html = data["gift-collection-data"];
      if (!html) return null;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const script = doc.querySelector("[data-gift-collection-data]");
      if (!script) return null;

      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  isInCollection(item, campaign, map) {
    const productCollections = map[String(item.product_id)] || [];
    const targets = this.safeArray(campaign.target_collections).map(id =>
      this.normalizeGidToNumeric(id)
    );

    return productCollections.some(id => targets.includes(String(id)));
  }

  filterTargetItems(cart, campaign, collectionData) {
    const items = this.safeArray(cart.items).filter(item => !this.isGiftItem(item));

    if (campaign.target_scope === "collection") {
      const map = this.normalizeMap(collectionData || {});
      return items.filter(item => this.isInCollection(item, campaign, map));
    }

    if (campaign.target_scope === "product") {
      const ids = this.safeArray(campaign.target_products).map(product =>
        this.normalizeGidToNumeric(product?.id || product)
      );
      return items.filter(item => ids.includes(String(item.product_id)));
    }

    if (campaign.target_scope === "vendor") {
      const vendors = this.safeArray(campaign.target_vendors).map(String);
      return items.filter(item => vendors.includes(String(item.vendor)));
    }

    return items;
  }

  checkCondition(cart, campaign, collectionData) {
    const items = this.filterTargetItems(cart, campaign, collectionData);

    if (campaign.condition_type === "quantity") {
      const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
      return quantity >= Number(campaign.condition_value);
    }

    if (campaign.condition_type === "amount") {
      const total = items.reduce((sum, item) => sum + item.original_line_price, 0);
      return total >= Number(campaign.condition_value) * 100;
    }

    return false;
  }

  getXCart() {
    try {
      const config = window.GIFT_ENGINE_CONFIG;
      const selectors = config?.cartSelectors || [
        '#CartDrawer [x-data]',
        '[x-data*="xCart"]'
      ];

      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element?.__x?.$data) return element.__x.$data;
      }
    } catch (error) {}

    return null;
  }

  findLineIndex(cart, lineKey) {
    const index = cart.items.findIndex(item => item.key === lineKey);
    return index >= 0 ? index + 1 : null;
  }

  getSectionsToRender() {
    const config = window.GIFT_ENGINE_CONFIG;

    if (config?.sections) {
      return config.sections;
    }

    try {
      return Alpine.store("xCartHelper").getSectionsToRender();
    } catch (error) {
      return [];
    }
  }

  async cartRequest(url, payload, meta = {}) {
    const sectionArray = this.getSectionsToRender();
    const sections = sectionArray.map(section => section.id);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({
        ...payload,
        sections,
        sections_url: window.location.pathname
      })
    });

    let parsedState = await response.json();

    try {
      if (parsedState?.cart_level_discount_applications) {
        const updated = await Alpine.store("xCartHelper").checkForEvepoint(parsedState, payload);
        if (updated && Object.keys(updated).length > 0) {
          parsedState = updated;
        }
      }
    } catch (error) {}

    try {
      const xCart = this.getXCart();
      if (xCart && parsedState) {
        xCart.updateCartUI(
          parsedState,
          sectionArray,
          meta.itemId || null,
          meta.line || null,
          meta.qty || null
        );
      }
    } catch (error) {}

    document.dispatchEvent(new CustomEvent("cart:refresh", { detail: parsedState }));
    return parsedState;
  }

  async addGift(variantId) {
    await this.cartRequest(
      "/cart/add.js",
      {
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              _gift: "true",
              _source: "gift_engine"
            }
          }
        ]
      },
      {
        itemId: variantId,
        qty: 1
      }
    );
  }

  async removeGift(lineKey, cart) {
    const item = cart.items.find(cartItem => cartItem.key === lineKey);
    if (!item) return;

    const line = this.findLineIndex(cart, lineKey);

    await this.cartRequest(
      "/cart/change.js",
      {
        id: lineKey,
        quantity: 0
      },
      {
        itemId: item.variant_id,
        line,
        qty: 0
      }
    );
  }

  async run() {
    if (this.now() - this.lastActionAt < this.ACTION_COOLDOWN_MS) return;
    if (this.isUpdating) return;

    const campaign = this.getCampaign();
    if (!campaign) return;

    this.isUpdating = true;

    try {
      const variantId = this.getGiftVariantId(campaign);
      if (!variantId) return;

      const tiers = this.getTiers();

      const isCollectionScope = campaign.target_scope === "collection";
      const [cart, collectionData] = await Promise.all([
        this.getCart(),
        isCollectionScope ? this.fetchCollectionData() : Promise.resolve(null)
      ]);

      const giftItem = this.findGiftItem(cart, variantId);

      if (tiers) {
        const total = campaign.kademe_kaynagi === "sepet_toplam"
          ? this.getCartTotal(cart)
          : await this.getTargetTotal(cart, campaign, collectionData);

        const activeTier = this.findActiveTier(tiers, total);

        if (activeTier && !giftItem) {
          this.lastActionAt = this.now();
          await this.addGift(variantId);
        } else if (!activeTier && giftItem) {
          this.lastActionAt = this.now();
          await this.removeGift(giftItem.key, cart);
        }
      } else {
        const conditionMet = this.checkCondition(cart, campaign, collectionData);

        if (conditionMet && !giftItem) {
          this.lastActionAt = this.now();
          await this.addGift(variantId);
        } else if (!conditionMet && giftItem) {
          this.lastActionAt = this.now();
          await this.removeGift(giftItem.key, cart);
        }
      }
    } catch (error) {
    } finally {
      this.isUpdating = false;
    }
  }

  isCartWriteUrl(url) {
    if (typeof url !== "string") return false;
    return url.includes("/cart/add") ||
           url.includes("/cart/change") ||
           url.includes("/cart/update") ||
           url.includes("/cart/clear");
  }

  patchFetch() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);

      try {
        if (self.isCartWriteUrl(args[0]) && !self.isUpdating) {
          document.dispatchEvent(new CustomEvent(self.INTERNAL_EVENT));
        }
      } catch (error) {}

      return response;
    };
  }

  patchXHR() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const self = this;

    XMLHttpRequest.prototype.open = function (...args) {
      this.addEventListener("load", function () {
        try {
          if (self.isCartWriteUrl(args[1]) && !self.isUpdating) {
            document.dispatchEvent(new CustomEvent(self.INTERNAL_EVENT));
          }
        } catch (error) {}
      });

      return originalOpen.apply(this, args);
    };
  }

  bindEvents() {
    document.addEventListener(this.EVENT_NAME, () => {
      setTimeout(() => this.run(), 50);
    });

    document.addEventListener(this.INTERNAL_EVENT, () => {
      setTimeout(() => this.run(), 50);
    });

    window.addEventListener("load", () => {
      setTimeout(() => this.run(), 150);
    });
  }

  init() {
    this.patchFetch();
    this.patchXHR();
    this.bindEvents();
  }
}

(() => {
  "use strict";
  const giftEngine = new GiftEngine();
  giftEngine.init();
})();